// hooks/useSessionTabs.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useRegistrationService,
  useACCourseService,
  RegistrationSessionModelView,
  ACCourseSessionModelView,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { buildSessionTabs, SessionTabEntry } from '../Tabs/buildSessionTabs';

export interface UseSessionTabsResult {
  tabs: SessionTabEntry[];
  loading: boolean;
  loaded: boolean;
  refetch: () => Promise<void>;
  /** Course session catalogue (fetched for orderNo lookup) — reusable for "Add Session" dropdowns. */
  courseSessions: ACCourseSessionModelView[];
  courseSessionsLoading: boolean;
  refetchCourseSessions: () => Promise<void>;
}

/**
 * Fetches a registration's sessions AND its course's session catalogue
 * (the latter purely to get orderNo per courseSessionID, since
 * RegistrationSessionModelView doesn't carry orderNo itself), then derives
 * the dynamic tab list so the CALLING page can render them as top-level
 * tabs, not nested inside another Tabs component.
 */
export function useSessionTabs(
  registrationId: number,
  courseId: number | undefined,
  active: boolean
): UseSessionTabsResult {
  const registrationService = useRegistrationService();
  const courseService = useACCourseService();

  const [sessions, setSessions] = useState<RegistrationSessionModelView[] | null>(null);
  const [loading, setLoading] = useState(false);

  const [courseSessions, setCourseSessions] = useState<ACCourseSessionModelView[]>([]);
  const [courseSessionsLoading, setCourseSessionsLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    if (!registrationId) return;
    setLoading(true);
    try {
      const res: any = await registrationService.getSessions(registrationId);
      const data: RegistrationSessionModelView[] = res?.data ?? res?.items ?? res ?? [];
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching registration sessions:', e);
      toast.error('Error', 'Could not load registration sessions.');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [registrationId, registrationService]);

  const fetchCourseSessions = useCallback(async () => {
    if (!courseId) return;
    setCourseSessionsLoading(true);
    try {
      const result = await courseService.getSessions(courseId);
      setCourseSessions(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error('Error fetching course sessions:', e);
      toast.error('Error', 'Could not load course sessions.');
    } finally {
      setCourseSessionsLoading(false);
    }
  }, [courseId, courseService]);

  useEffect(() => {
    if (active && sessions === null) fetchSessions();
  }, [active, sessions, fetchSessions]);

  useEffect(() => {
    if (active && courseId) fetchCourseSessions();
  }, [active, courseId, fetchCourseSessions]);

  const orderNoByCourseSessionID = useMemo(() => {
    const map = new Map<number, number>();
    for (const cs of courseSessions) map.set(Number(cs.courseSessionID), Number(cs.orderNo));
    return map;
  }, [courseSessions]);

  const tabs = useMemo(
    () => (sessions ? buildSessionTabs(sessions, orderNoByCourseSessionID) : []),
    [sessions, orderNoByCourseSessionID]
  );

  return {
    tabs,
    loading,
    loaded: sessions !== null,
    refetch: fetchSessions,
    courseSessions,
    courseSessionsLoading,
    refetchCourseSessions: fetchCourseSessions,
  };
}